<template>
  <app-modal ref="modal" close-on-backdrop-click>
    <div class="locale-select-modal-wrapper flex--column--align-center localized-text">
      <header class="locale-select-modal-wrapper__header header flex--space-between--center">
        <h2 class="header__title">{{ $t('localeSelect.title') }}</h2>
        <close-button
          v-modal-cancel
          class="locale-select-modal-wrapper__close-button"
        ></close-button>
      </header>

      <div class="locale-select-modal-wrapper__input locale-select-modal-wrapper__country-input">
        <u-dropdown
          v-if="isCountrySelectionEnabled"
          v-model="selectedCountry"
          :options="countriesOptions"
          :label="$t('checkout.input.shippingToLabel')"
          class="locale-select-dropdown"
        >
          <template #value>{{ $t(selectedCountry.label) }}</template>
          <template #option="{ option }">
            {{ $t(option.label) }}
          </template>
        </u-dropdown>

        <u-input
          v-else
          :value="selectedCountryDisplayValue"
          :label="$t('checkout.input.shippingToLabel')"
          disabled
        />
      </div>

      <div class="locale-select-modal-wrapper__input locale-select-modal-wrapper__language-input">
        <u-dropdown
          v-model="selectedLocale"
          :options="localesOptions"
          :label="$t('localeSelectModal.language')"
        >
          <template #value>{{ $t(selectedLocale.label) }}</template>
          <template #option="{ option }">
            {{ $t(option.label) }}
          </template>
        </u-dropdown>
      </div>

      <app-button
        ref="submitButton"
        class="locale-select-modal-wrapper__save-button"
        :is-loading="isLoading"
        @click="onSave"
        >{{ $t('localeSelectModal.button.save') }}</app-button
      >
    </div>
  </app-modal>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import { path } from 'ramda';

import { UDropdown, UInput } from 'universkin-shared';
import CloseButton from '@/components/common/CloseButton.vue';
import AppButton from '@/components/common/AppButton.vue';
import AppModal from '@/components/common/AppModal.vue';

import { sortInAlphabeticalOrder } from '@/utils/common';
import { displayNameMixin } from '@/mixins/displayNameMixin';

import { SUPPORTED_LOCALES_SELECT_OPTIONS, SUPPORTED_LOCALES } from '@/constants/locale';
import { types } from '@/store/types';

const COUNTRY_LABEL = 'Shipping to';

const LANGUAGE_LABEL = 'localeSelectModal.language';

export default {
  name: 'LocaleSelectModal',
  components: { CloseButton, AppButton, AppModal, UDropdown, UInput },
  mixins: [displayNameMixin],
  inputLabels: { COUNTRY_LABEL, LANGUAGE_LABEL },
  data() {
    return {
      selectedCountry: null,
      selectedLocale: null,
      isLoading: false
    };
  },
  computed: {
    ...mapState({
      selectedLocaleCode: types.state.LOCALE,
      countries: types.state.AVAILABLE_DELIVERY_COUNTRIES
    }),
    ...mapGetters({
      isCountrySelectionEnabled: types.getters.IS_MULTICOUNTRY_DELIVERY_AVAILABLE,
      selectedCountryCode: types.getters.DELIVERY_COUNTRY
    }),
    countriesOptions() {
      return this.countries
        .map(this.getCountry)
        .sort(({ label: previousCountryLabel }, { label: currentCountryLabel }) =>
          sortInAlphabeticalOrder(this.$t(previousCountryLabel), this.$t(currentCountryLabel))
        );
    },
    localesOptions() {
      return SUPPORTED_LOCALES.map(code => SUPPORTED_LOCALES_SELECT_OPTIONS[code]);
    },
    selectedCountryDisplayValue() {
      return this.selectedCountry ? this.$t(this.selectedCountry.label) : '';
    }
  },

  methods: {
    ...mapActions({
      setLocale: types.actions.SET_LOCALE,
      setCountry: types.actions.SET_DELIVERY_COUNTRY
    }),
    async onSave() {
      const countryCode = path(['value'], this.selectedCountry);
      const localeCode = path(['value'], this.selectedLocale);

      this.setLoading(true);
      await this.setCountry(countryCode);
      await this.setLocale(localeCode);
      this.setLoading(false);

      // TODO: implement imperative submit in v-modal library instead
      this.$refs.submitButton.$el.dispatchEvent(new CustomEvent('modal-submit', { bubbles: true }));
    },
    setLoading(isLoading) {
      this.isLoading = isLoading;
    },
    getCountry(countryCode) {
      return {
        label: this.getCountryDisplayName(countryCode),
        value: countryCode
      };
    },
    open() {
      this.selectedCountry = this.getCountry(this.selectedCountryCode);
      this.selectedLocale = SUPPORTED_LOCALES_SELECT_OPTIONS[this.selectedLocaleCode];

      return this.$refs.modal.open();
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/colors';

.locale-select-modal-wrapper {
  padding: 32px 32px 60px;
  background-color: $white-color;
  --dropdown-options-height: 130px;
  width: 670px;
  max-width: 100%;
  font-family: var(--u-font-family-Helvetica);

  &__header {
    width: 100%;
  }

  &__input {
    margin-bottom: 24px;
    width: 100%;
    font-weight: 400;
  }

  &__country-input {
    margin-bottom: 24px;
  }

  &__save-button {
    align-self: flex-start;
    width: 100%;
    min-width: 62px;
    min-height: 48px;
    font-size: 1.6rem;
    text-transform: uppercase;
  }
}

.header {
  margin-bottom: 24px;

  &__title {
    width: 200px;
    font-size: 20px;
    font-style: normal;
    font-weight: 400;
    line-height: 32px;
    letter-spacing: 0.4px;
    color: $tuatara-color;
    text-align: left;
  }
}

@media (min-width: 600px) {
  .header {
    &__title {
      width: auto;
    }
  }
}
</style>
